/**
 * Global floating panel for background lead-document uploads.
 * Stays visible across routes while files upload.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  leadDocumentUploadManager,
  type LeadUploadBatch,
  getBatchProgressPct,
  formatBatchEta,
  formatBatchSummary,
  formatUploadBytes,
} from '../../utils/leadDocumentUploadManager';
import { CheckCircle2, ChevronDown, ChevronUp, Loader2, AlertTriangle, X, Upload } from 'lucide-react';
import './LeadDocumentUploadBanner.css';

function BatchCard({ batch, expanded, onToggle, onDismiss }: {
  batch: LeadUploadBatch;
  expanded: boolean;
  onToggle: () => void;
  onDismiss: () => void;
}) {
  const progress = getBatchProgressPct(batch);
  const current = batch.files.find(f => f.status === 'uploading')
    ?? batch.files.find(f => f.status === 'pending');
  const failed = batch.files.filter(f => f.status === 'error').length;
  const done = batch.files.filter(f => f.status === 'complete').length;

  return (
    <div className={`ldu-card ldu-card--${batch.status}`}>
      <div className="ldu-card-head">
        <span className="ldu-card-icon" aria-hidden>
          {batch.status === 'running' ? <Loader2 size={16} className="spin" /> : batch.status === 'complete' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
        </span>
        <div className="ldu-card-main">
          <div className="ldu-card-title-row">
            <Link to={`/client/${batch.leadId}`} className="ldu-card-lead" title="Open lead workspace">
              {batch.leadLabel}
            </Link>
            {batch.status === 'running' && (
              <span className="ldu-card-eta">{formatBatchEta(batch)}</span>
            )}
          </div>
          <div className="ldu-card-summary">{formatBatchSummary(batch)}</div>
          {batch.status === 'running' && current && (
            <div className="ldu-card-current" title={current.fileName}>
              Uploading: {current.fileName}
              {current.fileSize > 0 && current.status === 'uploading' && (
                <span className="ldu-card-current-pct">
                  {' '}({Math.round((current.bytesTransferred / current.fileSize) * 100)}%)
                </span>
              )}
            </div>
          )}
          {batch.status === 'complete' && (
            <div className="ldu-card-done">All {done} file{done === 1 ? '' : 's'} uploaded — safe to leave this page.</div>
          )}
          {batch.status === 'error' && (
            <div className="ldu-card-done ldu-card-done--warn">
              {done} uploaded, {failed} failed — open the lead to retry failed files.
            </div>
          )}
        </div>
        <div className="ldu-card-actions">
          <button type="button" className="ldu-icon-btn" onClick={onToggle} aria-expanded={expanded} title={expanded ? 'Hide file list' : 'Show file list'}>
            {expanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
          {batch.status !== 'running' && (
            <button type="button" className="ldu-icon-btn" onClick={onDismiss} title="Dismiss">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="ldu-progress" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
        <div className="ldu-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {expanded && (
        <ul className="ldu-file-list">
          {batch.files.map(f => (
            <li key={f.id} className={`ldu-file-row ldu-file-row--${f.status}`}>
              <span className="ldu-file-name" title={f.fileName}>{f.fileName}</span>
              <span className="ldu-file-meta">
                {formatUploadBytes(f.fileSize)}
                {f.status === 'uploading' && f.fileSize > 0 && (
                  <> · {Math.round((f.bytesTransferred / f.fileSize) * 100)}%</>
                )}
                {f.status === 'complete' && ' · Done'}
                {f.status === 'error' && ` · ${f.error || 'Failed'}`}
                {f.status === 'pending' && ' · Queued'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const LeadDocumentUploadBanner: React.FC = () => {
  const [batches, setBatches] = useState<LeadUploadBatch[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => leadDocumentUploadManager.subscribe(setBatches), []);

  const visible = useMemo(
    () => batches.filter(b => b.status === 'running' || (b.finishedAt && Date.now() - b.finishedAt < 30 * 60 * 1000)),
    [batches],
  );

  if (visible.length === 0) return null;

  return (
    <div className="ldu-root" role="region" aria-label="Background file uploads">
      <div className="ldu-root-head">
        <Upload size={14} />
        <span>Lead uploads</span>
        {visible.some(b => b.status === 'running') && (
          <span className="ldu-root-hint">Continues in the background if you navigate away</span>
        )}
      </div>
      {visible.map(batch => (
        <BatchCard
          key={batch.batchId}
          batch={batch}
          expanded={expanded[batch.batchId] ?? batch.status === 'running'}
          onToggle={() => setExpanded(prev => ({ ...prev, [batch.batchId]: !prev[batch.batchId] }))}
          onDismiss={() => leadDocumentUploadManager.dismissBatch(batch.batchId)}
        />
      ))}
    </div>
  );
};

export default LeadDocumentUploadBanner;
